
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Image, HomeIcon, CreditCard, CalendarDays } from 'lucide-react';
import { useForm } from "react-hook-form";

const PurchasePromotion = () => {
  const [selectedPromotion, setSelectedPromotion] = useState('story');
  const [duration, setDuration] = useState(7);
  const [isClinicSponsored, setIsClinicSponsored] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(99);
  const form = useForm();

  // Calculate price based on promotion type and duration
  const calculatePrice = (type: string, days: number, sponsored: boolean) => {
    let basePrice = 0;
    switch (type) {
      case 'story':
        basePrice = days * 14; // $14 per day
        break;
      case 'beforeAfter':
        basePrice = days * 19; // $19 per day
        break;
      case 'homepage':
        basePrice = days * 25; // $25 per day
        break;
      default:
        basePrice = 0;
    }
    
    // Apply discount if sponsored by clinic
    return sponsored ? Math.round(basePrice * 0.8) : basePrice;
  };

  // Update price when any option changes
  const updatePrice = (type = selectedPromotion, days = duration, sponsored = isClinicSponsored) => {
    const newPrice = calculatePrice(type, days, sponsored);
    setCalculatedPrice(newPrice);
  };

  const handlePromotionTypeChange = (value: string) => {
    setSelectedPromotion(value);
    updatePrice(value);
  };

  const handleDurationChange = (value: string) => {
    const days = parseInt(value);
    setDuration(days);
    updatePrice(selectedPromotion, days);
  };

  const handleSponsorChange = (checked: boolean) => {
    setIsClinicSponsored(checked);
    updatePrice(selectedPromotion, duration, checked);
  };

  const handleCheckout = () => {
    // In a real app, this would connect to Stripe or another payment processor
    console.log('Processing payment for:', {
      type: selectedPromotion,
      duration,
      isClinicSponsored,
      price: calculatedPrice
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Purchase New Promotion</CardTitle>
          <CardDescription>
            Boost your visibility to attract new patients
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4">Select Promotion Type</h3>
              <Tabs defaultValue="story" onValueChange={handlePromotionTypeChange}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="story" className="flex flex-col py-4">
                    <Star className="h-6 w-6 mb-2" />
                    Featured Story
                  </TabsTrigger>
                  <TabsTrigger value="beforeAfter" className="flex flex-col py-4">
                    <Image className="h-6 w-6 mb-2" />
                    Before/After
                  </TabsTrigger>
                  <TabsTrigger value="homepage" className="flex flex-col py-4">
                    <HomeIcon className="h-6 w-6 mb-2" />
                    Homepage
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="story">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium">Featured Story</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Your content will appear in the top slots of the stories feed
                    </p>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      <li>Get up to 5x more views</li>
                      <li>Appear at the top of the story feed</li>
                      <li>Highlight your best cases</li>
                    </ul>
                  </div>
                </TabsContent>
                
                <TabsContent value="beforeAfter">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium">Featured Before/After</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Showcase your best work in the before/after gallery
                    </p>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      <li>Appear in the top row of the gallery</li>
                      <li>Show up in search results first</li>
                      <li>Highlight dramatic transformations</li>
                    </ul>
                  </div>
                </TabsContent>
                
                <TabsContent value="homepage">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium">Homepage Spotlight</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Your profile will appear in the featured doctors section
                    </p>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      <li>Appear in the top 3 doctors on homepage</li>
                      <li>Increased visibility to all users</li>
                      <li>Profile badge showing "Featured Doctor"</li>
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4">Choose Duration</h3>
              <RadioGroup 
                defaultValue="7" 
                className="grid grid-cols-4 gap-4"
                onValueChange={handleDurationChange}
              >
                <div>
                  <RadioGroupItem value="7" id="d-7" className="peer sr-only" />
                  <Label 
                    htmlFor="d-7" 
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <CalendarDays className="mb-2 h-6 w-6" />
                    <span className="text-lg font-medium">7</span>
                    <span className="text-xs">days</span>
                  </Label>
                </div>
                
                <div>
                  <RadioGroupItem value="14" id="d-14" className="peer sr-only" />
                  <Label 
                    htmlFor="d-14" 
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <CalendarDays className="mb-2 h-6 w-6" />
                    <span className="text-lg font-medium">14</span>
                    <span className="text-xs">days</span>
                  </Label>
                </div>
                
                <div>
                  <RadioGroupItem value="30" id="d-30" className="peer sr-only" />
                  <Label 
                    htmlFor="d-30" 
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <CalendarDays className="mb-2 h-6 w-6" />
                    <span className="text-lg font-medium">30</span>
                    <span className="text-xs">days</span>
                  </Label>
                </div>
                
                <div>
                  <RadioGroupItem value="90" id="d-90" className="peer sr-only" />
                  <Label 
                    htmlFor="d-90" 
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <CalendarDays className="mb-2 h-6 w-6" />
                    <span className="text-lg font-medium">90</span>
                    <span className="text-xs">days</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox 
                  id="sponsoredByClinic" 
                  checked={isClinicSponsored}
                  onCheckedChange={handleSponsorChange}
                />
                <label
                  htmlFor="sponsoredByClinic"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I want this promotion sponsored by my clinic
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                *Clinic sponsored promotions appear with your clinic's branding and may be subsidized by your clinic
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="w-full p-4 bg-muted/30 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total Price</span>
              <span className="text-2xl font-bold">${calculatedPrice}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isClinicSponsored && "Includes 20% clinic sponsorship discount"}
            </p>
          </div>
          <Button className="w-full" size="lg" onClick={handleCheckout}>
            <CreditCard className="mr-2 h-4 w-4" />
            Proceed to Payment
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PurchasePromotion;
